import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  ArrowRight,
  Calendar,
  MapPin,
  Palette,
  Image,
  Music,
  Users,
  Check,
  Upload,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palettes
const COLOR_PALETTES = [
  { id: "champagne-gold", name: "Champagne & Or", colors: ["#F9F6F0", "#C5A028", "#EEDEA8", "#1C1917"] },
  { id: "blush-rose", name: "Rose Poudré", colors: ["#FDF2F8", "#EC4899", "#FBCFE8", "#831843"] },
  { id: "sage-green", name: "Vert Sauge", colors: ["#F0FDF4", "#22C55E", "#BBF7D0", "#14532D"] },
  { id: "navy-gold", name: "Bleu Nuit & Or", colors: ["#1E3A5F", "#C5A028", "#EEDEA8", "#0F172A"] },
  { id: "lavender", name: "Lavande", colors: ["#FAF5FF", "#A855F7", "#E9D5FF", "#581C87"] },
  { id: "terracotta", name: "Terracotta", colors: ["#FEF3E2", "#EA580C", "#FED7AA", "#7C2D12"] },
  { id: "minimal-white", name: "Minimaliste Blanc", colors: ["#FFFFFF", "#1C1917", "#E5E5E5", "#404040"] },
  { id: "burgundy", name: "Bordeaux", colors: ["#FDF2F8", "#9F1239", "#FECDD3", "#4C0519"] }
];

// Themes/Styles
const THEMES = [
  { id: "romantic", name: "Romantique", description: "Doux, fleuri, élégant" },
  { id: "modern", name: "Moderne", description: "Épuré, minimaliste, tendance" },
  { id: "classic", name: "Classique", description: "Intemporel, raffiné" },
  { id: "bohemian", name: "Bohème", description: "Nature, liberté, authenticité" },
  { id: "glamour", name: "Glamour", description: "Luxueux, scintillant" },
  { id: "rustic", name: "Champêtre", description: "Naturel, chaleureux" }
];

// Features/Options
const FEATURES = [
  { id: "animation", name: "Animation d'ouverture", description: "Effet enveloppe ou porte élégante" },
  { id: "music", name: "Musique personnalisée", description: "Votre chanson préférée intégrée" },
  { id: "rsvp", name: "Gestion RSVP", description: "Suivi des confirmations en temps réel" },
  { id: "maps", name: "Google Maps", description: "Localisation du lieu intégrée" },
  { id: "gallery", name: "Galerie photos", description: "Vos plus belles photos de couple" },
  { id: "countdown", name: "Compte à rebours", description: "Décompte jusqu'au jour J" }
];

const PersonnalisationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventType = searchParams.get("evenement") || "";
  const packType = searchParams.get("pack") || "";
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Event details
    type_evenement: eventType,
    pack: packType,
    nom: "",
    email: "",
    telephone: "",
    
    // Step 2 - Event info
    date_evenement: null,
    heure_evenement: "",
    lieu_nom: "",
    lieu_adresse: "",
    lieu_ville: "",
    
    // Step 3 - Design preferences
    couleur_palette: "",
    theme: "",
    features: [],
    
    // Step 4 - Content
    noms_maries: "",
    message_accueil: "",
    programme: "",
    infos_supplementaires: "",
    photos_description: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/personnalisation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          date_evenement: formData.date_evenement 
            ? format(formData.date_evenement, "dd/MM/yyyy") 
            : null,
          features: formData.features.join(", ")
        })
      });

      if (!response.ok) throw new Error("Erreur lors de l'envoi");

      toast.success("Merci ! Votre demande de personnalisation a bien été envoyée. Nous vous contacterons sous 24h.");
      navigate("/confirmation-demande");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.type_evenement && formData.nom && formData.email;
      case 2:
        return formData.date_evenement && formData.lieu_ville;
      case 3:
        return formData.couleur_palette && formData.theme;
      case 4:
        return formData.noms_maries || formData.message_accueil;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-stone-900">
            Mon Invitation Magique
          </Link>
          <Link to="/" className="text-sm text-stone-500 hover:text-[#C5A028] flex items-center gap-2">
            <ArrowLeft size={16} />
            Retour
          </Link>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`flex items-center ${s < 4 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s 
                      ? 'bg-[#C5A028] text-white' 
                      : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-[#C5A028]' : 'bg-stone-100'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span>Informations</span>
            <span>Événement</span>
            <span>Design</span>
            <span>Contenu</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-8">
              <div className="text-center mb-12">
                <span className="text-sm uppercase tracking-[0.2em] text-[#C5A028] mb-4 block">
                  Étape 1 sur 4
                </span>
                <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
                  Parlez-nous de vous
                </h1>
                <p className="text-stone-600 max-w-md mx-auto">
                  Ces informations nous permettront de vous contacter pour finaliser votre invitation.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100 max-w-xl mx-auto">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Type d'événement *
                    </label>
                    <Select
                      value={formData.type_evenement}
                      onValueChange={(value) => setFormData({ ...formData, type_evenement: value })}
                    >
                      <SelectTrigger className="border-stone-200" data-testid="select-event-type">
                        <SelectValue placeholder="Sélectionnez votre événement" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mariage">Mariage</SelectItem>
                        <SelectItem value="anniversaire">Anniversaire</SelectItem>
                        <SelectItem value="baby-shower">Baby Shower</SelectItem>
                        <SelectItem value="bapteme">Baptême</SelectItem>
                        <SelectItem value="fiancailles">Fiançailles</SelectItem>
                        <SelectItem value="autre">Autre événement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Votre nom complet *
                    </label>
                    <Input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      placeholder="Marie & Pierre Dupont"
                      className="border-stone-200"
                      data-testid="input-nom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="border-stone-200"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Téléphone
                    </label>
                    <Input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="06 12 34 56 78"
                      className="border-stone-200"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Event Details */}
          {step === 2 && (
            <div className="space-y-8">
              <div className="text-center mb-12">
                <span className="text-sm uppercase tracking-[0.2em] text-[#C5A028] mb-4 block">
                  Étape 2 sur 4
                </span>
                <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
                  Les détails de votre événement
                </h1>
                <p className="text-stone-600 max-w-md mx-auto">
                  Quand et où se déroule votre événement ?
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100 max-w-xl mx-auto">
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Date de l'événement *
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal border-stone-200"
                            data-testid="date-picker"
                          >
                            <Calendar className="mr-2 h-4 w-4 text-stone-500" />
                            {formData.date_evenement 
                              ? format(formData.date_evenement, "dd MMMM yyyy", { locale: fr })
                              : <span className="text-stone-500">Choisir une date</span>
                            }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={formData.date_evenement}
                            onSelect={(date) => setFormData({ ...formData, date_evenement: date })}
                            locale={fr}
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Heure
                      </label>
                      <Input
                        type="time"
                        value={formData.heure_evenement}
                        onChange={(e) => setFormData({ ...formData, heure_evenement: e.target.value })}
                        className="border-stone-200"
                        data-testid="input-time"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Nom du lieu
                    </label>
                    <Input
                      type="text"
                      value={formData.lieu_nom}
                      onChange={(e) => setFormData({ ...formData, lieu_nom: e.target.value })}
                      placeholder="Château de Versailles, Domaine des Roses..."
                      className="border-stone-200"
                      data-testid="input-lieu-nom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Adresse complète
                    </label>
                    <Input
                      type="text"
                      value={formData.lieu_adresse}
                      onChange={(e) => setFormData({ ...formData, lieu_adresse: e.target.value })}
                      placeholder="123 rue des Fleurs"
                      className="border-stone-200"
                      data-testid="input-adresse"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Ville *
                    </label>
                    <Input
                      type="text"
                      value={formData.lieu_ville}
                      onChange={(e) => setFormData({ ...formData, lieu_ville: e.target.value })}
                      placeholder="Paris"
                      className="border-stone-200"
                      data-testid="input-ville"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Design Preferences */}
          {step === 3 && (
            <div className="space-y-8">
              <div className="text-center mb-12">
                <span className="text-sm uppercase tracking-[0.2em] text-[#C5A028] mb-4 block">
                  Étape 3 sur 4
                </span>
                <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
                  Choisissez votre style
                </h1>
                <p className="text-stone-600 max-w-md mx-auto">
                  Personnalisez l'apparence de votre invitation digitale.
                </p>
              </div>

              {/* Color Palettes */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
                <h3 className="font-serif text-xl text-stone-900 mb-6 flex items-center gap-2">
                  <Palette className="text-[#C5A028]" size={20} />
                  Palette de couleurs *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {COLOR_PALETTES.map((palette) => (
                    <button
                      key={palette.id}
                      onClick={() => setFormData({ ...formData, couleur_palette: palette.id })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        formData.couleur_palette === palette.id
                          ? 'border-[#C5A028] shadow-md'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                      data-testid={`palette-${palette.id}`}
                    >
                      <div className="flex gap-1 mb-3">
                        {palette.colors.map((color, i) => (
                          <div
                            key={i}
                            className="w-6 h-6 rounded-full border border-stone-200"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <p className="text-sm font-medium text-stone-700">{palette.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Themes */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
                <h3 className="font-serif text-xl text-stone-900 mb-6 flex items-center gap-2">
                  <Sparkles className="text-[#C5A028]" size={20} />
                  Style / Thème *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {THEMES.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => setFormData({ ...formData, theme: theme.id })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.theme === theme.id
                          ? 'border-[#C5A028] bg-[#FDFBF7]'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                      data-testid={`theme-${theme.id}`}
                    >
                      <p className="font-medium text-stone-900">{theme.name}</p>
                      <p className="text-xs text-stone-500 mt-1">{theme.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
                <h3 className="font-serif text-xl text-stone-900 mb-6 flex items-center gap-2">
                  <Music className="text-[#C5A028]" size={20} />
                  Options souhaitées
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {FEATURES.map((feature) => (
                    <div
                      key={feature.id}
                      className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                        formData.features.includes(feature.id)
                          ? 'border-[#C5A028] bg-[#FDFBF7]'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                      onClick={() => handleFeatureToggle(feature.id)}
                    >
                      <Checkbox
                        checked={formData.features.includes(feature.id)}
                        onCheckedChange={() => handleFeatureToggle(feature.id)}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="font-medium text-stone-900 text-sm">{feature.name}</p>
                        <p className="text-xs text-stone-500">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Content */}
          {step === 4 && (
            <div className="space-y-8">
              <div className="text-center mb-12">
                <span className="text-sm uppercase tracking-[0.2em] text-[#C5A028] mb-4 block">
                  Étape 4 sur 4
                </span>
                <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
                  Le contenu de votre invitation
                </h1>
                <p className="text-stone-600 max-w-md mx-auto">
                  Personnalisez les textes et partagez vos photos.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100 max-w-2xl mx-auto">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Noms à afficher *
                    </label>
                    <Input
                      type="text"
                      value={formData.noms_maries}
                      onChange={(e) => setFormData({ ...formData, noms_maries: e.target.value })}
                      placeholder="Marie & Pierre"
                      className="border-stone-200"
                      data-testid="input-noms"
                    />
                    <p className="text-xs text-stone-500 mt-1">Tel qu'il apparaîtra sur l'invitation</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Message d'accueil
                    </label>
                    <Textarea
                      value={formData.message_accueil}
                      onChange={(e) => setFormData({ ...formData, message_accueil: e.target.value })}
                      placeholder="Nous avons l'honneur de vous inviter à célébrer notre union..."
                      className="border-stone-200 min-h-[100px]"
                      data-testid="input-message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Programme de la journée
                    </label>
                    <Textarea
                      value={formData.programme}
                      onChange={(e) => setFormData({ ...formData, programme: e.target.value })}
                      placeholder="14h30 : Cérémonie civile&#10;16h00 : Cérémonie religieuse&#10;18h00 : Vin d'honneur&#10;20h00 : Dîner"
                      className="border-stone-200 min-h-[120px]"
                      data-testid="input-programme"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Informations supplémentaires
                    </label>
                    <Textarea
                      value={formData.infos_supplementaires}
                      onChange={(e) => setFormData({ ...formData, infos_supplementaires: e.target.value })}
                      placeholder="Dress code, hébergement, parking, liste de mariage..."
                      className="border-stone-200"
                      data-testid="input-infos"
                    />
                  </div>

                  <div className="bg-[#FDFBF7] rounded-xl p-6">
                    <div className="flex items-start gap-3">
                      <Upload className="text-[#C5A028] mt-1" size={20} />
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          Vos photos
                        </label>
                        <p className="text-sm text-stone-600 mb-3">
                          Décrivez les photos que vous souhaitez intégrer (couple, fiançailles, lieu...). 
                          Vous pourrez nous les envoyer par email après validation.
                        </p>
                        <Textarea
                          value={formData.photos_description}
                          onChange={(e) => setFormData({ ...formData, photos_description: e.target.value })}
                          placeholder="Ex: Photo de notre séance de fiançailles, photo du lieu de réception..."
                          className="border-stone-200 bg-white"
                          data-testid="input-photos"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-12 max-w-2xl mx-auto">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="border-stone-200"
            >
              <ArrowLeft size={16} className="mr-2" />
              Précédent
            </Button>
          ) : (
            <div />
          )}

          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#0A0A0A] hover:bg-stone-800 text-white"
              data-testid="btn-next"
            >
              Suivant
              <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !canProceed()}
              className="bg-[#C5A028] hover:bg-[#b89422] text-white"
              data-testid="btn-submit"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande"}
              <Sparkles size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default PersonnalisationPage;
